/* eslint-disable @typescript-eslint/no-explicit-any */

import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal, ModalProps } from 'antd'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useQrCodesServiceGetQrCodeEndpoint,
  useQrCodesServiceGetQrCodeEndpointKey
} from 'src/api/queries'
import { Permissions, QrCodeType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message } from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  onCancel: () => void
} & ModalProps

const QRCodeTypeMap: any = {
  [QrCodeType.PERSON]: {
    permission: Permissions.PERSONS_MENU_ACCESS_PERSONS,
    getLink: (id: string) => `persons/details/${id}`
  },
  [QrCodeType.TREATMENT]: {
    permission: Permissions.PERSONS_MENU_ACCESS_PERSONS,
    getLink: (id: string) => {
      const [personId, treatmentId] = id.split(' ')

      return `patients/details/${personId}/treatments/${treatmentId}`
    }
  },
  [QrCodeType.INVESTIGATION_DEMAND]: {
    permission: Permissions.INVESTIGATIONS_MENU_ACCESS_TESTS,
    getLink: (id: string) => `tests/details/${id}`
  },
  [QrCodeType.MEDICAL_RECEIPT]: {
    permission: Permissions.TREATMENT_MENU_ACCESS_PRESCRIPTIONS,
    getLink: (id: string) => {
      const [treatmentId, recipeId] = id.split(' ')

      return `prescriptions/${treatmentId}/${recipeId}`
    }
  },
  [QrCodeType.LABORATORY_TEST]: {
    permission: Permissions.INVESTIGATIONS_MENU_ACCESS_TESTS,
    getLink: (id: string) => {
      const [investigationId, testId] = id.split(' ')

      return `tests/details/${investigationId}/test/${testId}`
    }
  },
  [QrCodeType.SPECIMEN]: {
    permission: Permissions.INVESTIGATIONS_MENU_ACCESS_TESTS,
    getLink: (id: string) => `tests/specimen-details/${id}`
  }
}

export const QRModal = ({ onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const { hasPermission } = useUser()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const code = Form.useWatch('code', form)

  const { data, refetch, isLoading, isSuccess, status } =
    useQrCodesServiceGetQrCodeEndpoint({ id: code }, [code], {
      enabled: false
    })

  const onSubmit = async () => {
    await form.validateFields()
    refetch()
  }

  const onModalClose = () => {
    onCancel()
    form.resetFields()
    queryClient.removeQueries({
      queryKey: [useQrCodesServiceGetQrCodeEndpointKey]
    })
  }

  useEffect(() => {
    if (status === 'error') message.error(t('qr_code.invalid_code'))
  }, [status])

  useEffect(() => {
    if (isSuccess && data?.type) {
      const qrCodeMapping = QRCodeTypeMap[data?.type]

      if (!qrCodeMapping) {
        message.error(t('qr_code.unsupported_qr_type'))
        return
      }

      const { permission, getLink } = qrCodeMapping
      if (hasPermission(permission)) {
        onModalClose()
        navigate(getLink(data?.content))
      } else {
        message.error(t('qr_code.required_permission'))
      }
    }
  }, [isSuccess])

  return (
    <Modal
      title={t('qr_code.scan_qr_code')}
      onCancel={onModalClose}
      maskClosable={false}
      open
      centered
      footer={null}
      width={360}
      destroyOnHidden
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Flex vertical gap={16} align="center">
          <Form.Item name="code" rules={requiredRules} className="full-width">
            <Input placeholder={t('qr_code.enter_code_manually')} />
          </Form.Item>

          <Flex justify="center" gap={8} className="mt-24">
            <Button onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="primary" onClick={onSubmit} loading={isLoading}>
              {t('common.confirm')}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
